import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  TextField,
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";

import { useConsulta } from "../hooks/useConsulta";
import SelectorBuscable from "../components/SelectorBuscable";
import {
  cambiarEstadoSolicitud,
  obtenerBloques,
  obtenerCausasRechazo,
  obtenerHorarioSolicitudes,
  obtenerRecursos,
  obtenerSalones,
} from "../services/bibliotecaApi";
import {
  ESTADO_PREPARADA,
  ESTADO_REVISADA,
  auxiliaresQueFaltan,
  ejemplaresQueFaltan,
} from "../model/gestion";
import { TIPO_ARIA } from "../constants";

const sinDatos = () => Promise.resolve([]);

/** Select sencillo sobre una lista { id, nombre }. */
const Selector = ({ etiqueta, valor, opciones, onChange, deshabilitado }) => (
  <TextField
    select
    fullWidth
    margin="normal"
    label={etiqueta}
    value={valor}
    disabled={deshabilitado}
    onChange={({ target }) => onChange(target.value)}
  >
    {opciones.map(({ id, nombre }) => (
      <MenuItem key={id} value={id}>
        {nombre}
      </MenuItem>
    ))}
  </TextField>
);

Selector.propTypes = {
  etiqueta: PropTypes.string.isRequired,
  valor: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  opciones: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  deshabilitado: PropTypes.bool,
};

Selector.defaultProps = { deshabilitado: false };

/**
 * Pestaña Gestionar: pasar una solicitud al estado siguiente. Las opciones son las `acciones`
 * que manda el backend en el detalle: solo las que el tipo permite desde el
 * estado actual y sobre las que esta persona tiene permiso.
 *
 * Como en AGIL:
 *   - rechazar pide una o varias causas de la lista y, si alguna es Otro, el
 *     detalle; el solicitante las ve en el historial;
 *   - revisar exige los auxiliares del tipo y, en ARIA, el recurso con que
 *     se da la capacitacion y, si es en sala de computo, la sala; y si el
 *     ARIA llego sin horas (lo pidio un estudiante), la fecha y las horas;
 *   - preparar (Libro a tu clase) exige los ejemplares asignados y el peso.
 * El backend vuelve a comprobarlo todo.
 */
const PanelGestionar = ({ solicitud, onGestionada, onMensaje }) => {
  const acciones = solicitud.acciones ?? [];
  // Con una sola opcion no hay nada que elegir.
  const [estado, setEstado] = useState(acciones.length === 1 ? acciones[0].codigo : "");
  const [mensaje, setMensaje] = useState("");
  const [causas, setCausas] = useState([]);
  const [recurso, setRecurso] = useState("");
  const [bloque, setBloque] = useState("");
  const [salon, setSalon] = useState("");
  const [peso, setPeso] = useState("");
  // El ARIA de un estudiante llega sin horas: al revisarlo, biblioteca pone
  // la fecha (arranca en la que propuso el estudiante, si propuso) y las horas.
  const [fecha, setFecha] = useState(solicitud.fecha_prestamo ?? "");
  const [inicio, setInicio] = useState("");
  const [fin, setFin] = useState("");
  const [enviando, setEnviando] = useState(false);

  const elegida = acciones.find(({ codigo }) => codigo === estado);
  const revisando = estado === ESTADO_REVISADA;
  const esAria = solicitud.tipo_solicitud?.codigo === TIPO_ARIA;
  const pideRecurso = revisando && esAria;
  const pideHorario = pideRecurso && !solicitud.hora_inicio;
  const { datos: horario } = useConsulta(
    pideHorario ? obtenerHorarioSolicitudes : () => Promise.resolve(null),
    [pideHorario],
    null,
  );
  const rechazando = Boolean(elegida?.requiere_motivo);

  const { datos: listaCausas } = useConsulta(
    rechazando ? obtenerCausasRechazo : sinDatos,
    [rechazando],
  );
  const pideDetalle = listaCausas.some(
    ({ id, requiere_detalle }) => requiere_detalle && causas.includes(id),
  );

  const { datos: recursos } = useConsulta(pideRecurso ? obtenerRecursos : sinDatos, [pideRecurso]);
  const recursoElegido = recursos.find(({ id }) => id === recurso);
  const pideSala = pideRecurso && Boolean(recursoElegido?.requiere_sala);
  const { datos: bloques } = useConsulta(pideSala ? obtenerBloques : sinDatos, [pideSala]);
  const { datos: salones, cargando: cargandoSalones } = useConsulta(
    () => (pideSala && bloque ? obtenerSalones(bloque) : sinDatos()),
    [pideSala, bloque],
  );

  const faltanAuxiliares = revisando ? auxiliaresQueFaltan(solicitud) : [];
  const preparando = estado === ESTADO_PREPARADA;
  const faltanEjemplares = preparando ? ejemplaresQueFaltan(solicitud) : "";
  const faltaPeso = preparando && !(Number(peso) > 0);
  const faltaMotivo = rechazando && (causas.length === 0 || (pideDetalle && !mensaje.trim()));
  const faltaRecurso = pideRecurso && (!recurso || (pideSala && (!bloque || !salon)));
  const horasAlReves = Boolean(inicio && fin && inicio >= fin);
  const faltaHorario = pideHorario && (!fecha || !inicio || !fin || horasAlReves);
  const bloqueado =
    !elegida ||
    faltaMotivo ||
    faltanAuxiliares.length > 0 ||
    faltaRecurso ||
    faltaHorario ||
    Boolean(faltanEjemplares) ||
    faltaPeso ||
    enviando;

  const gestionar = async () => {
    setEnviando(true);
    const extra = pideRecurso
      ? {
          recurso,
          bloque: bloque || null,
          salon: salon || null,
          ...(pideHorario ? { fecha_prestamo: fecha, hora_inicio: inicio, hora_fin: fin } : {}),
        }
      : preparando
        ? { peso: Number(peso) }
        : rechazando
          ? { causas }
          : {};
    const { ok, titulo, datos } = await cambiarEstadoSolicitud(
      solicitud.id,
      estado,
      mensaje.trim(),
      extra,
    );
    if (!ok) {
      onMensaje(titulo, "info");
      setEnviando(false);
      return;
    }
    onMensaje(`La solicitud quedó ${elegida.nombre.toLowerCase()}.`, "success");
    onGestionada(datos);
  };

  if (acciones.length === 0) {
    return (
      <Alert severity="info">
        En el estado {solicitud.estado_actual?.nombre?.toLowerCase()} no tienes un
        siguiente paso para esta solicitud. Revisa tus permisos en Controlar Permisos.
      </Alert>
    );
  }

  return (
    <div>
      <FormControl component="fieldset" fullWidth>
        <FormLabel component="legend">
          Estado actual: {solicitud.estado_actual?.nombre}. Pasar a:
        </FormLabel>
        {/* Casillas como en Gestion Practicas; se elige una sola. */}
        <List dense disablePadding>
          {acciones.map(({ codigo, nombre }) => (
            <ListItem key={codigo} dense button onClick={() => setEstado(codigo)}>
              <ListItemIcon>
                <Checkbox edge="start" checked={estado === codigo} tabIndex={-1} disableRipple />
              </ListItemIcon>
              <ListItemText primary={nombre} />
            </ListItem>
          ))}
        </List>
      </FormControl>

      {faltanAuxiliares.length > 0 && (
        <Alert severity="warning" style={{ marginTop: 8 }}>
          Para revisarla asigna al menos un auxiliar de {faltanAuxiliares.join(" y uno de ")},
          en la pestaña Auxiliares.
        </Alert>
      )}

      {faltanEjemplares && (
        <Alert severity="warning" style={{ marginTop: 8 }}>
          Para prepararla: {faltanEjemplares.toLowerCase()}, en la pestaña Ejemplares.
        </Alert>
      )}

      {preparando && (
        <TextField
          fullWidth
          type="number"
          margin="normal"
          label="Peso de los libros (gramos)"
          inputProps={{ min: 1 }}
          value={peso}
          onChange={({ target }) => setPeso(target.value)}
        />
      )}

      {pideHorario && (
        <>
          <Alert severity="info" style={{ marginTop: 8 }}>
            Este ARIA lo pidió un estudiante y no tiene horas. Define la fecha y las horas de la
            capacitación.
            {solicitud.fecha_prestamo && ` El estudiante propuso el ${solicitud.fecha_prestamo}.`}
            {horario?.atencion.texto && ` Biblioteca atiende ${horario.atencion.texto}.`}
          </Alert>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="date"
                margin="normal"
                label="Fecha"
                InputLabelProps={{ shrink: true }}
                value={fecha}
                onChange={({ target }) => setFecha(target.value)}
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField
                fullWidth
                type="time"
                margin="normal"
                label="Hora de inicio"
                InputLabelProps={{ shrink: true }}
                value={inicio}
                error={horasAlReves}
                onChange={({ target }) => setInicio(target.value)}
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField
                fullWidth
                type="time"
                margin="normal"
                label="Hora de fin"
                InputLabelProps={{ shrink: true }}
                value={fin}
                error={horasAlReves}
                helperText={horasAlReves ? "Debe ser después del inicio" : ""}
                onChange={({ target }) => setFin(target.value)}
              />
            </Grid>
          </Grid>
        </>
      )}

      {pideRecurso && (
        <>
          <Selector
            etiqueta="Recurso para la capacitación"
            valor={recurso}
            opciones={recursos}
            onChange={(valor) => {
              setRecurso(valor);
              setBloque("");
              setSalon("");
            }}
          />
          {pideSala && (
            <>
              <SelectorBuscable
                etiqueta="Bloque"
                valor={bloque}
                opciones={bloques}
                margin="normal"
                onChange={(valor) => {
                  setBloque(valor);
                  setSalon("");
                }}
              />
              <SelectorBuscable
                etiqueta="Sala"
                valor={salon}
                opciones={salones}
                margin="normal"
                deshabilitado={!bloque}
                cargando={cargandoSalones}
                onChange={setSalon}
              />
            </>
          )}
        </>
      )}

      {rechazando && (
        <TextField
          select
          fullWidth
          required
          margin="normal"
          label="Causas del rechazo"
          value={causas}
          SelectProps={{
            multiple: true,
            renderValue: (elegidas) =>
              listaCausas
                .filter(({ id }) => elegidas.includes(id))
                .map(({ nombre }) => nombre)
                .join(", "),
          }}
          onChange={({ target }) => setCausas(target.value)}
        >
          {listaCausas.map(({ id, nombre }) => (
            <MenuItem key={id} value={id}>
              <Checkbox size="small" checked={causas.includes(id)} />
              <ListItemText primary={nombre} />
            </MenuItem>
          ))}
        </TextField>
      )}

      <TextField
        fullWidth
        multiline
        rows={3}
        margin="normal"
        label={
          !rechazando ? "Observación (opcional)" : pideDetalle ? "Detalle" : "Detalle (opcional)"
        }
        required={pideDetalle}
        error={pideDetalle && !mensaje.trim()}
        helperText={
          rechazando
            ? pideDetalle
              ? "Con la causa Otro debes escribir el detalle. El solicitante lo verá en el historial."
              : "El solicitante verá las causas y el detalle en el historial de su solicitud."
            : ""
        }
        value={mensaje}
        onChange={({ target }) => setMensaje(target.value)}
      />

      <div style={{ textAlign: "right", marginTop: 8 }}>
        <Button color="primary" variant="contained" disabled={bloqueado} onClick={gestionar}>
          {enviando ? "Guardando..." : "Gestionar"}
        </Button>
      </div>
    </div>
  );
};

PanelGestionar.propTypes = {
  solicitud: PropTypes.shape({
    id: PropTypes.number.isRequired,
    estado_actual: PropTypes.object,
    tipo_solicitud: PropTypes.object,
    fecha_prestamo: PropTypes.string,
    hora_inicio: PropTypes.string,
    auxiliares: PropTypes.array,
    ejemplares: PropTypes.array,
    acciones: PropTypes.arrayOf(
      PropTypes.shape({
        codigo: PropTypes.string.isRequired,
        nombre: PropTypes.string.isRequired,
        requiere_motivo: PropTypes.bool,
      }),
    ),
  }).isRequired,
  /** Recibe el detalle actualizado que responde el backend. */
  onGestionada: PropTypes.func.isRequired,
  onMensaje: PropTypes.func.isRequired,
};

export default PanelGestionar;
